import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { gameTime } from "./clock";
import "./GameCenter.css";
import "./EuroGameCenter.css";

const STAT_COLS = [["min", "MIN"], ["pts", "PTS"], ["reb", "REB"], ["ast", "AST"], ["fg3m", "3PM"], ["pir", "PIR"]];
const fmt = (v, k) => (v == null ? "–" : k === "min" ? Math.round(v) : Math.round(Number(v)));

function Scoreboard({ game }) {
  const started = game.status === "in" || game.status === "post";
  const t = gameTime(game);
  return (
    <div className="tv-scoreboard gc-board">
      <div className="tv-score-top">
        <span>{game.status === "in" ? "In progress" : game.status === "pre" ? "Pre-game" : "Final"}</span>
        <span className={`tv-clock ${t.critical ? "is-critical" : ""}`}>{t.label}</span><span className="tv-venue" />
      </div>
      <div className="tv-score-line">
        <span className="tv-big-team"><span className="tv-big-logo"><img src={game.away_logo} alt="" /></span><b>{game.away_code}</b><small>AWAY</small></span>
        <strong data-testid="egc-score">{started ? `${game.away_pts ?? 0} – ${game.home_pts ?? 0}` : "VS"}</strong>
        <span className="tv-big-team"><span className="tv-big-logo"><img src={game.home_logo} alt="" /></span><b>{game.home_code}</b><small>HOME</small></span>
      </div>
    </div>
  );
}

function TeamBox({ code, players, onOpenPlayer }) {
  const rows = [...(players || [])].sort((a, b) => (b.starter === a.starter ? (b.pir ?? 0) - (a.pir ?? 0) : b.starter - a.starter));
  return (
    <div className="egc-team" data-testid={`egc-team-${code}`}>
      <div className="egc-team-head">{code}</div>
      <div className="egc-box-row egc-box-head"><span>Player</span>{STAT_COLS.map(([k, l]) => <span key={k}>{l}</span>)}</div>
      {rows.map((p) => (
        <button key={p.player_id} type="button" className="egc-box-row" onClick={() => onOpenPlayer?.(p.player_id)} data-testid={`egc-player-${p.player_id}`}>
          <span className="egc-pl">{p.starter && <i className="egc-dot" />}{p.name}</span>
          {STAT_COLS.map(([k]) => <span key={k} className="egc-num">{fmt(p[k], k)}</span>)}
        </button>
      ))}
    </div>
  );
}

const dec = (v) => (v == null ? "–" : Number(v).toFixed(2));

function MarketOdds({ game, odds }) {
  if (!odds || !odds.market) {
    return <div className="gc-section"><div className="gc-section-head"><h3>Market · assos365</h3></div><p className="tv-empty">No market odds for this game yet.</p></div>;
  }
  const m = odds.market, e = odds.edge, mdl = odds.model;
  const Row = ({ label, children }) => <div className="egc-odds-row"><span className="egc-odds-k">{label}</span>{children}</div>;
  return (
    <div className="gc-section" data-testid="egc-market">
      <div className="gc-section-head"><h3>Market · assos365</h3>{odds.vendor && <span className="egc-vendor">{odds.vendor.home} v {odds.vendor.away}</span>}</div>
      {mdl && <div className="egc-model">Model: <b>{game.away_code} {mdl.away_pts}</b> – <b>{mdl.home_pts} {game.home_code}</b> · total <b>{mdl.total}</b> · margin <b>{mdl.margin > 0 ? "+" : ""}{mdl.margin}</b></div>}
      {m.moneyline && <Row label="Moneyline"><span className={`egc-od ${e?.moneyline?.model_pick === "away" ? "is-pick" : ""}`}>{game.away_code} {dec(m.moneyline.away)}</span><span className={`egc-od ${e?.moneyline?.model_pick === "home" ? "is-pick" : ""}`}>{game.home_code} {dec(m.moneyline.home)}</span></Row>}
      {m.spread && <Row label={`Spread ${m.spread.line > 0 ? "+" : ""}${m.spread.line}`}><span className={`egc-od ${e?.spread?.lean === "away" ? "is-pick" : ""}`}>{game.away_code} {dec(m.spread.away)}</span><span className={`egc-od ${e?.spread?.lean === "home" ? "is-pick" : ""}`}>{game.home_code} {dec(m.spread.home)}</span>{e?.spread?.value && <span className="egc-val" data-testid="egc-value-spread">VALUE {e.spread.lean}</span>}</Row>}
      {m.total && <Row label={`Total ${m.total.line}`}><span className={`egc-od ${e?.total?.lean === "over" ? "is-pick" : ""}`}>O {dec(m.total.over)}</span><span className={`egc-od ${e?.total?.lean === "under" ? "is-pick" : ""}`}>U {dec(m.total.under)}</span>{e?.total?.value && <span className="egc-val" data-testid="egc-value-total">VALUE {e.total.lean} ({e.total.diff > 0 ? "+" : ""}{e.total.diff})</span>}</Row>}
    </div>
  );
}

export function EuroGameCenter({ game, comp, odds, onOpenPlayer }) {
  const [box, setBox] = useState(null);
  const id = game?.id;
  const started = game && (game.status === "in" || game.status === "post");
  useEffect(() => {
    if (!id || !started) { setBox(null); return undefined; }
    let alive = true;
    setBox(undefined);
    api.get(`/euro/box/${id}`).then((r) => alive && setBox(r.data)).catch(() => alive && setBox(null));
    return () => { alive = false; };
  }, [id, started]);
  if (!game) return <section className="tv-panel tv-center"><p className="tv-empty">Select a game.</p></section>;
  const teams = box?.teams || [];
  const away = teams.find((t) => t.code === game.away_code);
  const home = teams.find((t) => t.code === game.home_code);
  return (
    <section className="tv-panel tv-center" aria-label="Game center" data-testid="tv-center-panel">
      <div className="tv-scroll">
        <Scoreboard game={game} />
        <MarketOdds game={game} odds={odds} />
        <div className="gc-section"><div className="gc-section-head"><h3>Box score</h3></div>
          {!started ? <p className="tv-empty">Box score appears after tip-off.</p>
            : box === undefined ? <p className="tv-empty">Loading box score…</p>
            : teams.length ? (
              <div className="egc-boxes">
                <TeamBox code={game.away_code} players={away?.players} onOpenPlayer={onOpenPlayer} />
                <TeamBox code={game.home_code} players={home?.players} onOpenPlayer={onOpenPlayer} />
              </div>
            ) : <p className="tv-empty">Box score not available for this game.</p>}
        </div>
      </div>
    </section>
  );
}
